import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { Component } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule, ActivatedRoute, Params } from '@angular/router';
import { tap, switchMap, of, catchError, timer } from 'rxjs';
import { BillService, TransactionResponse } from '../bill.service';
import { IBillData } from '../modal';

@Component({
  selector: 'app-bill',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, HttpClientModule, RouterModule],
  templateUrl: './bill.component.html',
  styleUrl: './bill.component.css'
})
export class BillComponent {
  billData: IBillData | null = null;
  transactionData: TransactionResponse | null = null;

  loading: boolean = true;
  submitting: boolean = false;
  polling: boolean = false;
  error: string | null = null;
  success: boolean = false;
  successMessage: string | null = null;

  formData = {
    amount: '',
    name: '',
    phone: '',
    description: ''
  };

  billId: string = ''; // Default bill ID for testing

  constructor(
    private route: ActivatedRoute,
    private billService: BillService
  ) { }



  ngOnInit(): void {
    this.route.params.pipe(
      tap((params: Params) => {
        console.log('Route Params:', params);
        this.billId = params['billNumber'];

        if (!this.billId) {
          this.error = 'Missing bill ID in URL';
          this.loading = false;
        }
      }),
      switchMap((params) => {
        return this.billId
          ? this.billService.getBillDetails(this.billId)
          : of(null);
      }),
      catchError((err: any) => {
        console.error(err);
        this.error = 'The bill could not be found or may have been removed.';
        this.loading = false;
        return of(null);
      })
    ).subscribe((data: IBillData | null) => {
      if (data) {
        this.billData = data;
        this.formData.amount = data.balance.toString();
        this.success = data.status === 'FullyPaid' || data.is_paid;
      }
      this.loading = false;
    });
  }


  generateOrderId(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let orderId = '';
    for (let i = 0; i < 11; i++) {
      orderId += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return orderId;
  }

  startLongPolling(rrn: string) {
    this.polling = true;
    const maxAttempts = 20;
    const pollInterval = 3000;

    let attempts = 0;

    timer(0, pollInterval).pipe(
      switchMap(() => {
        if (attempts >= maxAttempts) {
          return of(null);
        }
        attempts++;
        return this.billService.getTrasactionStatus(rrn);
      }),
      catchError(err => {
        console.error('[v0] Polling error:', err);
        return of(null);
      })
    ).subscribe(data => {
      if (!data) {
        if (attempts >= maxAttempts) {
          this.polling = false;
          this.successMessage = 'Payment submitted. Please check back later for confirmation.';
        }
        return;
      }

      this.transactionData = data;
      if (this.transactionData.status.toLowerCase() === 'success' || this.transactionData.status.toLowerCase() === 'failed') {
        this.polling = false;

        this.billService.getBillDetails(this.billId).subscribe(bill => {
          this.billData = bill;
          if (bill.is_paid || bill.status === 'FullyPaid') {
            this.success = true;
            this.successMessage = 'Payment successful! Your bill has been fully paid.';
          } else {
            this.successMessage = 'Payment status: ' + this.transactionData?.status;
          }
          this.polling = false;
        });
      }

    });
  }

  onSubmit(event: Event) {
    event.preventDefault();
    if (!this.billData) return;

    this.submitting = true;
    this.error = null;
    this.successMessage = null;

    const payload = {
      purpose: 'payment',
      order_id: this.generateOrderId(),
      amount: Number.parseFloat(this.formData.amount),
      callback_url: 'https://eoptclazivphhwk.m.pipedream.net',
      description: this.formData.description,
      wallet: this.billData.wallet,
      channel: 'Mpesa',
      bill_ref: this.billData.number,
      data: {
        phone_number: this.formData.phone,
        account_name: this.formData.name,
        code: '2001'
      }
    };

    this.billService.initiatePayment(payload).pipe(
      catchError(err => {
        console.error('[v0] Payment error:', err);
        this.error = err.error.message[0] || 'Failed to submit payment. Please try again.';
        this.submitting = false;
        return of(null);
      })
    ).subscribe((response) => {
      if (response) {
        console.log({ response });

        this.successMessage = 'Payment submitted successfully';
        this.formData = {
          amount: this.billData?.balance.toString() || '',
          name: '',
          phone: '',
          description: ''
        };
        this.submitting = false;
        if (response.rrn) {
          this.startLongPolling(response.rrn);
        }
      }
    });
  }
}
